import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { getBillingPlans, getBillingStatus, startCheckout, getCheckoutStatus, billingSelfTest, billingPortal } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { useAuth } from "@/context/AuthContext";
import { Check, CreditCard, ShieldCheck, Lightning, Stethoscope, Warning, CheckCircle, Gear, Flask } from "@phosphor-icons/react";
import { toast } from "sonner";

const HIGHLIGHT = "growth";

export default function BillingPage() {
  const { activeId, activeClient } = useClient();
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);

  const plansQ = useQuery({ queryKey: ["billing-plans"], queryFn: getBillingPlans });
  const statusQ = useQuery({ queryKey: ["billing-status", activeId], queryFn: () => getBillingStatus(activeId), enabled: !!activeId });
  const plans = plansQ.data?.plans || [];
  const testMode = plansQ.data?.test_mode;
  const sub = statusQ.data?.subscription || {};

  // Handle return from Stripe Checkout
  useEffect(() => {
    const sid = params.get("session_id");
    const cancelled = params.get("checkout");
    if (cancelled === "cancelled") { toast.info("Checkout cancelled."); setParams({}, { replace: true }); return; }
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await getCheckoutStatus(sid);
        if (r.payment_status === "paid") { toast.success("Subscription active — thank you!"); statusQ.refetch(); setParams({}, { replace: true }); return; }
        if (r.status === "expired" || tries >= 6) { toast.error("Payment not completed."); setParams({}, { replace: true }); return; }
        tries += 1; setTimeout(poll, 2000);
      } catch { toast.error("Could not verify payment."); setParams({}, { replace: true }); }
    };
    toast.loading("Verifying payment…", { id: "pay" });
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const subscribe = async (planId) => {
    if (!activeId) return toast.error("Select a workspace first");
    setBusy(planId);
    try {
      const r = await startCheckout({ plan_id: planId, interval, workspace_id: activeId, origin_url: window.location.origin });
      if (r.url) window.location.href = r.url;
      else toast.error("Could not start checkout");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed";
      toast.error(msg, { duration: 8000 });
    }
    setBusy(null);
  };

  const openPortal = async () => {
    if (!activeId) return;
    setPortalBusy(true);
    try {
      const r = await billingPortal(activeId, window.location.href);
      if (r.url) window.location.href = r.url;
      else toast.error("Could not open billing portal");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
    }
    setPortalBusy(false);
  };

  const runSelfTest = async () => {
    setDiagBusy(true);
    try {
      const r = await billingSelfTest();
      setDiag(r);
      if (r.ok) toast.success("Billing self-test passed — Stripe is configured correctly.");
      else toast.warning(`${r.issues?.length || 0} issue(s) found. See details below.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Self-test failed");
    }
    setDiagBusy(false);
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Billing & Subscription" title="Plans & Billing"
        subtitle={`Manage the subscription for ${activeClient?.name || "this workspace"}. Secure checkout by Stripe.`} />

      {plansQ.data && (testMode ? (
        <div data-testid="billing-test-banner" className="card-matte border border-amber-500/30 bg-amber-500/5 p-4 mb-5 flex items-start gap-3">
          <Flask size={20} className="text-amber-400 mt-0.5 shrink-0" weight="fill" />
          <div className="text-sm">
            <div className="font-head font-semibold text-amber-300">TEST / STAGING MODE — no real charges</div>
            <p className="text-amber-200/80 mt-0.5">Stripe is running on a <b>test</b> key. Subscribe with test card <b>4242 4242 4242 4242</b>, any future expiry, any CVC &amp; ZIP. Real cards are rejected here. Your production site (vozelto.com) uses live keys for real billing.</p>
          </div>
        </div>
      ) : (
        <div data-testid="billing-live-banner" className="card-matte border border-emerald-500/25 bg-emerald-500/5 p-4 mb-5 flex items-start gap-3">
          <ShieldCheck size={20} className="text-emerald-400 mt-0.5 shrink-0" weight="fill" />
          <div className="text-sm">
            <div className="font-head font-semibold text-emerald-300">LIVE billing</div>
            <p className="text-emerald-200/80 mt-0.5">Real payments are active. Stripe does not accept test cards in live mode — use the staging/preview environment (configured with Stripe test keys) to test safely without a real card.</p>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5" data-testid="billing-header">
        <div className="card-matte px-4 py-3" data-testid="current-subscription">
          <div className="text-[10px] label-caps text-[#7a7a7a]">Current plan</div>
          <div className="text-lg font-head font-semibold capitalize">
            {sub.plan ? `${sub.plan} · ${sub.interval || ""}` : "No active subscription"}
            {sub.status === "active" && <span className="ml-2 text-xs text-emerald-400">● active</span>}
            {sub.status === "past_due" && <span className="ml-2 text-xs text-amber-400">● past due</span>}
            {sub.status === "canceled" && <span className="ml-2 text-xs text-[#9a9a9a]">● canceled</span>}
          </div>
          {sub.plan && (
            <Button data-testid="manage-billing-btn" onClick={openPortal} disabled={portalBusy}
              className="mt-2 h-8 text-xs bg-white/10 hover:bg-white/15 text-white">
              <Gear size={13} className="mr-1.5" /> {portalBusy ? "Opening…" : "Manage / Cancel Billing"}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {testMode && <span data-testid="test-mode-badge" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/25 text-amber-400"><ShieldCheck size={13} weight="fill" /> Test Mode</span>}
          <div className="flex rounded-lg bg-white/5 p-1" data-testid="interval-toggle">
            <button data-testid="interval-monthly" onClick={() => setInterval("monthly")} className={`px-4 py-1.5 rounded-md text-sm ${interval === "monthly" ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>Monthly</button>
            <button data-testid="interval-yearly" onClick={() => setInterval("yearly")} className={`px-4 py-1.5 rounded-md text-sm ${interval === "yearly" ? "bg-[#6B3DFF] text-white" : "text-[#9a9a9a]"}`}>Yearly <span className="text-[10px] opacity-80">2 mo free</span></button>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map((p) => {
          const price = interval === "monthly" ? p.monthly : p.yearly;
          const active = sub.plan === p.id && sub.status === "active";
          const featured = p.id === HIGHLIGHT;
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`card-matte p-6 flex flex-col relative ${featured ? "ring-2 ring-[#6B3DFF]" : ""}`}>
              {featured && <span className="absolute -top-2 right-4 text-[10px] px-2 py-0.5 rounded-full bg-[#6B3DFF] text-white flex items-center gap-1"><Lightning size={11} weight="fill" /> Popular</span>}
              <div className="font-head font-bold text-xl">{p.name}</div>
              <div className="mt-2 mb-4">
                <span className="text-3xl font-head font-bold">${price}</span>
                <span className="text-sm text-[#7a7a7a]">/{interval === "monthly" ? "mo" : "yr"}</span>
                {interval === "yearly" && (
                  <div className="text-[11px] text-emerald-400 mt-1">
                    ≈ ${Math.round(p.yearly / 12)}/mo · save ${Math.round(p.monthly * 12 - p.yearly)}/yr
                  </div>
                )}
              </div>
              <ul className="space-y-2 flex-1">
                {(p.features || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#cfcfcf]"><Check size={15} className="brand-text mt-0.5 shrink-0" weight="bold" /> {f}</li>
                ))}
              </ul>
              <Button data-testid={`subscribe-${p.id}`} onClick={() => subscribe(p.id)} disabled={busy === p.id || active}
                className={`mt-5 ${featured ? "bg-[#6B3DFF] hover:bg-[#A56CFF]" : "bg-white/10 hover:bg-white/15"} text-white`}>
                <CreditCard size={15} className="mr-1.5" />
                {active ? "Current Plan" : busy === p.id ? "Redirecting…" : "Subscribe"}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#7a7a7a] mt-5" data-testid="billing-cancel-note">You may cancel your subscription at any time. Your subscription remains active until the end of your current billing period.</p>
      <p className="text-xs text-[#7a7a7a] mt-2">Payments are processed securely by Stripe. {testMode && "Currently in test mode — use Stripe test cards (e.g. 4242 4242 4242 4242)."}</p>

      {isAdmin && (
        <div className="card-matte p-5 mt-8" data-testid="billing-diagnostics">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2 font-head font-semibold"><Stethoscope size={17} className="brand-text" /> Stripe Self-Test</div>
              <p className="text-xs text-[#7a7a7a] mt-1 max-w-xl">Owner-only. Validates your live key and every configured Price ID against Stripe (recurring? live mode? valid?) without charging anything. Run this in production to see the exact reason checkout fails.</p>
            </div>
            <Button data-testid="run-self-test" onClick={runSelfTest} disabled={diagBusy} className="bg-white/10 hover:bg-white/15 text-white">
              <Stethoscope size={15} className="mr-1.5" /> {diagBusy ? "Testing…" : "Run Self-Test"}
            </Button>
          </div>

          {diag && (
            <div className="mt-4 space-y-3" data-testid="self-test-results">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-full bg-white/5">Key: <b>{diag.key_prefix || "none"}</b></span>
                <span className="px-2.5 py-1 rounded-full bg-white/5">Source: <b>{diag.resolved_from || "—"}</b></span>
                <span className={`px-2.5 py-1 rounded-full ${diag.is_live ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>{diag.is_live ? "LIVE mode" : "TEST mode"}</span>
                {diag.key_valid === true && <span className="px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400">Key valid</span>}
                {diag.key_valid === false && <span className="px-2.5 py-1 rounded-full bg-red-500/10 text-red-400">Key invalid</span>}
              </div>

              {diag.issues?.length > 0 ? (
                <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-3" data-testid="self-test-issues">
                  <div className="flex items-center gap-1.5 text-amber-400 text-sm font-semibold mb-1.5"><Warning size={15} weight="fill" /> {diag.issues.length} issue(s) to fix</div>
                  <ul className="space-y-1 text-xs text-amber-200/90 list-disc pl-5">
                    {diag.issues.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 text-emerald-400 text-sm" data-testid="self-test-ok"><CheckCircle size={16} weight="fill" /> All checks passed — checkout should work.</div>
              )}

              {diag.prices?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[#7a7a7a] text-left">
                      <tr><th className="py-1.5 pr-3">Plan</th><th className="pr-3">Interval</th><th className="pr-3">Env var</th><th className="pr-3">Price ID</th><th className="pr-3">Recurring</th><th className="pr-3">Live</th><th>Status</th></tr>
                    </thead>
                    <tbody className="text-[#cfcfcf]">
                      {diag.prices.map((p, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="py-1.5 pr-3 capitalize">{p.plan}</td>
                          <td className="pr-3">{p.interval}</td>
                          <td className="pr-3 font-mono text-[10px] text-[#9a9a9a]">{p.env_var}</td>
                          <td className="pr-3 font-mono text-[10px]">{p.price_id || "—"}</td>
                          <td className="pr-3">{p.configured ? (p.recurring ? "✅" : "⚠️ one-time") : "—"}</td>
                          <td className="pr-3">{p.configured && p.valid ? (p.livemode ? "live" : "test") : "—"}</td>
                          <td>{!p.configured ? <span className="text-[#7a7a7a]">not set</span> : p.valid ? <span className="text-emerald-400">valid</span> : <span className="text-red-400">{p.error || "invalid"}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </PageContainer>
  );
}
